/**
 * useInviteUser Hook
 *
 * React Query mutation hook for inviting a user to the tenant.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { userService } from '../../../api/user.service';
import type { UserInvitationDTO, InvitationSanitized } from '../../../types/user.types';
import { getErrorMessage } from '../../../lib/utils/errors';

/**
 * Hook for inviting a user
 */
export const useInviteUser = (
  tenantId: string
): UseMutationResult<InvitationSanitized, Error, UserInvitationDTO> => {
  const queryClient = useQueryClient();

  return useMutation<InvitationSanitized, Error, UserInvitationDTO>({
    mutationFn: (data) => {
      console.log('[useInviteUser] Inviting user:', data.email);
      return userService.inviteUser(tenantId, data);
    },
    onSuccess: (invitation) => {
      console.log('[useInviteUser] User invited successfully:', invitation.email);
      // Invalidate users list to refresh the data
      queryClient.invalidateQueries({ queryKey: ['users', tenantId] });
      // Invalidate invitations list
      queryClient.invalidateQueries({ queryKey: ['invitations', tenantId] });
    },
    onError: (error) => {
      console.error('[useInviteUser] Failed to invite user:', getErrorMessage(error));
    },
  });
};
